import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosService } from 'shared/helpers/axios';
import useSWR from 'swr';

const fetcher = async (url) => {
  const res = await axiosService.get(url);
  return res.data;
};

const EditConsortium = () => {
  const { consortiumId } = useParams();
  const navigate = useNavigate();
  
  // Fetch consortium data
  const { data: consortium, error, mutate } = useSWR(
    consortiumId ? `/consortium/consortiums/${consortiumId}/` : null,
    fetcher
  );

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    status: 'forming',
    duration_months: '',
  });
  
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize form data when consortium loads
  useEffect(() => {
    if (consortium) {
      setFormData({
        name: consortium.name || '',
        status: consortium.status || 'forming',
        duration_months: consortium.duration_months || '',
      });
    }
  }, [consortium]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Prepare data for update
      const updateData = {
        name: formData.name,
        status: formData.status,
        duration_months: formData.duration_months ? parseInt(formData.duration_months) : null,
      };

      // Update consortium
      const response = await axiosService.patch(
        `/consortium/consortiums/${consortiumId}/`,
        updateData
      );

      setSaveSuccess(true);
      mutate(response.data); // Update SWR cache
      
      // Navigate back after short delay
      setTimeout(() => {
        navigate(`/consortium/${consortiumId}/`);
      }, 1500);
    } catch (err) {
      console.error('Error updating consortium:', err);
      setSaveError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        Object.values(err.response?.data || {}).flat().join(', ') ||
        'Failed to update consortium. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/consortium/${consortiumId}/`);
  };

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Failed to load consortium. {error.message}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/consortium//')}
          sx={{ mt: 2 }}
        >
          Back to Consortiums
        </Button>
      </Container>
    );
  }

  if (!consortium) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const statusOptions = [
    { value: 'forming', label: 'Forming' },
    { value: 'active', label: 'Active' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'completed', label: 'Completed' },
    { value: 'disbanded', label: 'Disbanded' }
  ];

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mb: 2 }}
        >
          Back to Consortium
        </Button>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Edit Consortium
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Update consortium information
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Consortium updated successfully! Redirecting...
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {saveError}
        </Alert>
      )}

      {/* Edit Form */}
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            {/* Consortium Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Consortium Name"
                value={formData.name}
                onChange={handleChange('name')}
                required
                helperText="The name identifying this partnership"
                disabled={saving}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={handleChange('status')}
                helperText="Current status of the consortium"
                disabled={saving}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Duration */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Duration (months)"
                value={formData.duration_months}
                onChange={handleChange('duration_months')}
                helperText="Project duration in months"
                disabled={saving}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Current Information */}
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Additional Information
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {new Date(consortium.created).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Last Modified
                </Typography>
                <Typography variant="body2">
                  {new Date(consortium.modified).toLocaleDateString()}
                </Typography>
              </Grid>
              {consortium.coordinator_member && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Coordinator
                  </Typography>
                  <Typography variant="body2">
                    {consortium.coordinator_member.name || 'N/A'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !formData.name.trim()}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Container>
  );
};

export default EditConsortium;
